import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, Copy, DollarSign, TrendingUp, ShoppingBag, Trophy, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/affiliate/me").then(({ data }) => setData(data)).catch(() => setData({ is_affiliate: false })); }, []);

  if (!data) return <div className="min-h-[60vh]" />;

  if (!data.is_affiliate) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center">
        <Trophy className="mx-auto text-ape-red mb-6" size={56} />
        <h1 className="font-display text-4xl tracking-tight">
          {data.status === "pending" ? "APPLICATION UNDER REVIEW" : "NOT AN AFFILIATE YET"}
        </h1>
        <p className="text-ape-muted mt-4">
          {data.status === "pending"
            ? <>Your affiliate application{data.code ? <> for code <span className="font-mono2 text-white">{data.code}</span></> : ""} is being reviewed. You'll get access to your dashboard once approved.</>
            : "Join the Ape-Tides affiliate program to earn commissions on every sale you drive."}
        </p>
        <Link to="/affiliate" className="inline-flex mt-8 bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-8 py-4 transition-colors duration-150" data-testid="apply-affiliate-btn">
          {data.status === "pending" ? "View Program" : "Apply Now"}
        </Link>
      </div>
    );
  }

  const refLink = `${window.location.origin}/shop?ref=${data.code}`;
  const owed = data.total_commission;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="affiliate-dashboard">
      <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">🦍 AFFILIATE DASHBOARD</span>
      <h1 className="font-display text-5xl tracking-tight mt-2 mb-8">WELCOME, {data.code}</h1>

      {/* Referral link */}
      <div className="border border-ape-border bg-ape-surface p-6 mb-8">
        <div className="flex items-center gap-2 font-mono2 text-[10px] tracking-widest text-ape-muted mb-3"><Link2 size={13} className="text-ape-red" /> YOUR UNIQUE REFERRAL LINK</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-ape-bg border border-ape-border px-4 py-3 font-mono2 text-sm text-white truncate" data-testid="referral-link">{refLink}</div>
          <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied!"); }}
            className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-sm px-5 py-3 flex items-center justify-center gap-2 transition-colors duration-150" data-testid="copy-link-btn">
            <Copy size={15} /> Copy
          </button>
        </div>
        <p className="text-ape-muted text-xs mt-3">Share this link. Customers get <span className="text-white">{data.customer_discount}% off</span> with your code <span className="font-mono2 text-ape-red">{data.code}</span>, and you earn <span className="text-white">{data.commission_rate}%</span> commission on every sale.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {[
          { icon: ShoppingBag, l: "Successful Sales", v: data.sales_count },
          { icon: TrendingUp, l: "Total Sales Volume", v: `$${data.total_sales_amount.toFixed(2)}` },
          { icon: DollarSign, l: "Commission Earned", v: `$${owed.toFixed(2)}` },
          { icon: Trophy, l: `Tier · ${data.tier.name}`, v: `${data.tier.rate}%` },
        ].map((s, i) => (
          <div key={i} className="border border-ape-border bg-ape-surface p-6">
            <s.icon className="text-ape-red" size={26} />
            <div className="font-display text-3xl mt-3">{s.v}</div>
            <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mt-1">{s.l.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Sales table */}
      <h2 className="font-display text-2xl tracking-wide mb-4">YOUR SALES</h2>
      {data.sales.length === 0 ? (
        <div className="border border-ape-border bg-ape-surface p-10 text-center text-ape-muted">No sales yet. Share your link and start earning.</div>
      ) : (
        <div className="border border-ape-border bg-ape-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-table">
            <thead>
              <tr className="border-b border-ape-border font-mono2 text-[10px] tracking-widest text-ape-muted">
                <th className="text-left p-4">ORDER</th><th className="text-left p-4">DATE</th>
                <th className="text-right p-4">SALE</th><th className="text-right p-4">COMMISSION</th>
              </tr>
            </thead>
            <tbody>
              {data.sales.map((s, i) => (
                <tr key={i} className="border-b border-ape-border last:border-0">
                  <td className="p-4 font-mono2">{s.order_num}</td>
                  <td className="p-4 text-ape-muted"><span className="inline-flex items-center gap-1"><Clock size={12} />{new Date(s.created_at).toLocaleDateString()}</span></td>
                  <td className="p-4 text-right font-mono2">${s.amount.toFixed(2)}</td>
                  <td className="p-4 text-right font-mono2 text-ape-red">+${s.commission.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
